import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { isAdminSelector } from '../../../components/store/auth/selectors';
import { showLoader, hideLoader } from '../../../components/store/common/slice';
import { changeCurrentPartnerShared } from '../../../components/store/admin/slice';
import { useAccessToken } from '../../../components/common/hooks/useAccessToken';
import { constants } from '../../../components/common/constants/constants';
import PartnersAndBuildingsDropdowns from '../../components/PartnersAndBuildingsDropdowns/PartnersAndBuildingsDropdowns';
import { StyledDropdownsWrapper, StyledButtonWrapper } from './AutoCampaignActions.styled';
import ConfirmationModal from './ConfirmationModal';

const AutoCampaignActions = () => {
    const isAdmin = useSelector(isAdminSelector);
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const client = useAccessToken();

    const [currentPartner, setCurrentPartner] = useState();
    const [currentBuildings, setCurrentBuildings] = useState([]);
    const [showComment, setShowComment] = useState(false);
    const [commentInfo, setCommentInfo] = useState({
        inCampaign: '',
        nextCampaign: ''
    });
    const [calculateBtnDisabled, setCalculateBtnDisabled] = useState(true);
    const [startButtonDisabled, setStartButtonDisabled] = useState(true);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [showErrorModal, setShowErrorModal] = useState(false);

    const onPartnerChange = e => {
        setCurrentPartner(e);
        setCalculateBtnDisabled(false)
        setStartButtonDisabled(true);
    };

    const onBuildingsChange = e => {
        setCurrentBuildings(e);
        setStartButtonDisabled(true);
    };

    const createPayloadArr = arr => {
        let options = [];
        arr.map(item => options.push(item.value))
        return options
    };

    const onCalculateClick = async () => {
        dispatch(showLoader());
        if (currentPartner.length === 1 && currentBuildings.length === 0) {
            const payload = {
                partnerID: currentPartner[0].value
            };
            const { result: { total } } = await client.get('/operations/auto-campaigns/coverage/partner', payload);
            setCommentInfo({
                inCampaign: total.inCampaign,
                nextCampaign: total.goalNotReached - total.inCampaign
            });
        } else if (currentPartner.length === 1 && currentBuildings.length > 0) {
            const payload = {
                partnerID: currentPartner[0].value,
                BuildingId: createPayloadArr(currentBuildings)
            };
            const { result: { total } } = await client.get('/operations/auto-campaigns/coverage/buildings', payload);
            setCommentInfo({
                inCampaign: total.inCampaign,
                nextCampaign: total.goalNotReached - total.inCampaign
            });
        } else {
            const payload = {
                // partnerID: createPayloadArr(currentPartner)
                partnerID: currentPartner[0].value
            };
            const { result: { total } } = await client.get('/operations/auto-campaigns/coverage/partner', payload);
            setCommentInfo({
                inCampaign: total.inCampaign,
                nextCampaign: total.goalNotReached - total.inCampaign
            });
        }
        setShowComment(true);
        // setStartButtonDisabled(false);
        dispatch(hideLoader());
    };

    const onStartClick = () => setShowConfirmModal(true);

    const hideConfirmModal = () => setShowConfirmModal(false);

    const hideErrorModal = () => setShowErrorModal(false);

    const history = useHistory();

    const startCampaign = async () => {
        dispatch(showLoader());
        let result;
        if (currentBuildings.length > 0) {
            const payload = {
                // partnerID: createPayloadArr(currentPartner),
                partnerID: currentPartner[0].value,
                BuildingId: createPayloadArr(currentBuildings),
                forceStart: true // will trigger the first step immediately
            };
            result = await client.put('/operations/auto-campaigns/new', payload);
        } else {
            const payload = {
                // partnerID: createPayloadArr(currentPartner),
                partnerID: currentPartner[0].value,
                forceStart: true // will trigger the first step immediately
            };
            result = await client.put('/operations/auto-campaigns/new_for_partner', payload);
        }

        //CHANGE FOR PROPER ERROR NAME
        if (result.error) {
            hideConfirmModal();
            setShowErrorModal(true);
        } else {
            dispatch(changeCurrentPartnerShared(currentPartner));
            hideConfirmModal();
            history.push({
                pathname: `${constants.pages.admin.autoCampaign}/status`
            });
        }
        dispatch(hideLoader());
    };

    const startCampaignAndFinishOld = async () => {
        dispatch(showLoader());
        let result;
        if (currentBuildings.length > 0) {
            const payload = {
                // partnerID: createPayloadArr(currentPartner),
                partnerID: currentPartner[0].value,
                BuildingId: createPayloadArr(currentBuildings),
                forceStart: true
            };
            result = await client.put('/operations/auto-campaigns/new', payload);
        } else {
            const payload = {
                // partnerID: createPayloadArr(currentPartner),
                partnerID: currentPartner[0].value,
                forceStart: true
            };
            result = await client.put('/operations/auto-campaigns/new_for_partner', payload);
        }

        if (result) {
            dispatch(changeCurrentPartnerShared(currentPartner));
            hideErrorModal();
            history.push({
                pathname: `${constants.pages.admin.autoCampaign}/status`
            });
        }
        dispatch(hideLoader());
    };

    if (!isAdmin) return <div>{t('searchPayments.noAdminRights')}</div>;

    return (
        <>
            <StyledDropdownsWrapper>
                <PartnersAndBuildingsDropdowns
                    onPartnerChange={onPartnerChange}
                    onBuildingsChange={onBuildingsChange}
                />

                <button
                    className='bllink-button'
                    onClick={onCalculateClick}
                    disabled={calculateBtnDisabled}
                >
                    {t('autoCampaign.actions.btns.calculate')}
                </button>

                {showComment && (
                    <div className='comment'>
                        {t('autoCampaign.actions.statistics', { currentPartner: currentPartner?.label, inCampaign: commentInfo.inCampaign, nextCampaign: commentInfo.nextCampaign })}
                    </div>
                )}
            </StyledDropdownsWrapper>
            <StyledButtonWrapper>
                <button
                    className='bllink-button green'
                    onClick={onStartClick}
                    disabled={startButtonDisabled}
                >
                    {t('autoCampaign.actions.btns.start')}
                </button>
            </StyledButtonWrapper>

            <ConfirmationModal
                show={showConfirmModal}
                onHide={hideConfirmModal}
                startCampaign={startCampaign}
            />

            <ConfirmationModal
                error
                show={showErrorModal}
                onHide={hideErrorModal}
                startCampaign={startCampaignAndFinishOld}
            />
        </>
    )
}

export default AutoCampaignActions;
